import { BookingCard } from '@components/Home/BookingCard';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingRecordListForCustomer, BookingStatus } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { PaginatedResponse } from 'noddi-types';
import { NoddiBasicCard, NoddiEmptyPage, NoddiSkeleton, NoddiTabs, TablePagination } from 'noddi-ui-web';
import { BookingsListTabs } from 'noddi-util';
import { useCustomerAppPagination } from '../../../hooks/useCustomerAppPagination';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { getCommonTranslations, getTabsTranslations } from '../../../utils/commonTranslations';

type Props = {
  userGroupId: number;
  carId: number;
};

export const CarBookings = ({ userGroupId, carId }: Props) => {
  const { currentBrand } = useCurrentBrand();
  const { pagination, setPagination } = useCustomerAppPagination();

  // Fetch all bookings
  const {
    isPending: isAllPending,
    data: allBookings,
    isFetching: isAllFetching,
    error: allBookingsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedBookingsForCustomer,
    input: {
      userGroupId,
      statusIds: [BookingStatus.CONFIRMED, BookingStatus.COMPLETED, BookingStatus.ASSIGNED_TO_ROUTE],
      pageSize: pagination.pageSize,
      pageIndex: pagination.pageIndex,
      brandDomains: [currentBrand],
      carIds: [carId]
    }
  });

  // Fetch upcoming bookings
  const {
    isPending: isUpcomingPending,
    data: upcomingBookings,
    isFetching: isUpcomingFetching,
    error: upcomingBookingsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedBookingsForCustomer,
    input: {
      userGroupId,
      isUpcoming: true,
      pageSize: pagination.pageSize,
      pageIndex: pagination.pageIndex,
      brandDomains: [currentBrand],
      carIds: [carId]
    }
  });

  // Fetch previous/completed bookings
  const {
    isPending: isPreviousPending,
    data: previousBookings,
    isFetching: isPreviousFetching,
    error: previousBookingsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedBookingsForCustomer,
    input: {
      userGroupId,
      isCompleted: true,
      pageSize: pagination.pageSize,
      pageIndex: pagination.pageIndex,
      brandDomains: [currentBrand],
      carIds: [carId]
    }
  });

  const handlePaginationChange = ({ pageIndex, pageSize }: { pageIndex: number; pageSize: number }) => {
    setPagination({ pageIndex, pageSize, search: pagination.search });
  };

  if (allBookingsError || upcomingBookingsError || previousBookingsError) {
    return null;
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-2'>
        <h6 className='text-xl font-bold text-primary md:text-3xl'>
          <Trans>Bookings</Trans>
        </h6>
        <span className='max-w-3xl text-sm text-balance sm:text-base xl:text-lg'>
          <Trans>Bookings for this car, including upcoming and prior bookings.</Trans>
        </span>
      </div>
      <NoddiTabs<BookingsListTabs>
        translations={getTabsTranslations()}
        tabItems={[
          {
            label: t`All`,
            value: BookingsListTabs.ALL,
            count: allBookings?.count ?? 0,
            node: (
              <CarBookingsList
                bookings={allBookings}
                isFetching={isAllFetching}
                isPending={isAllPending}
                pagination={pagination}
                onPaginationChange={handlePaginationChange}
                emptyStateMessage={t`No bookings for this car.`}
              />
            )
          },
          {
            label: t`Upcoming`,
            value: BookingsListTabs.UPCOMING,
            count: upcomingBookings?.count ?? 0,
            node: (
              <CarBookingsList
                bookings={upcomingBookings}
                isFetching={isUpcomingFetching}
                isPending={isUpcomingPending}
                pagination={pagination}
                onPaginationChange={handlePaginationChange}
                emptyStateMessage={t`No upcoming bookings for this car.`}
                isUpcoming
              />
            )
          },
          {
            label: t`Prior`,
            value: BookingsListTabs.PREVIOUS,
            count: previousBookings?.count ?? 0,
            node: (
              <CarBookingsList
                bookings={previousBookings}
                isFetching={isPreviousFetching}
                isPending={isPreviousPending}
                pagination={pagination}
                onPaginationChange={handlePaginationChange}
                emptyStateMessage={t`No previous bookings for this car.`}
              />
            )
          }
        ]}
        defaultValue={BookingsListTabs.ALL}
        keepExistingSearchParams
      />
    </div>
  );
};

type CarBookingsListProps = {
  bookings: PaginatedResponse<BookingRecordListForCustomer[]> | undefined;
  isFetching: boolean;
  isPending: boolean;
  pagination: { pageIndex: number; pageSize: number; search?: string };
  onPaginationChange: (params: { pageIndex: number; pageSize: number }) => void;
  emptyStateMessage: string;
  isUpcoming?: boolean;
};

const CarBookingsList = ({
  bookings,
  isFetching,
  isPending,
  pagination,
  onPaginationChange,
  emptyStateMessage,
  isUpcoming
}: CarBookingsListProps) => {
  if (isPending) {
    return <NoddiSkeleton className='h-32' />;
  }

  if (!bookings || bookings.count === 0) {
    return <NoddiEmptyPage className='rounded-xl bg-card' title={t`No bookings`} message={emptyStateMessage} />;
  }

  return (
    <div className='flex w-full flex-col gap-2'>
      {bookings.results.map((booking) => (
        <BookingCard key={booking.id} booking={booking} isUpcomingBooking={isUpcoming} />
      ))}
      {bookings.count > pagination.pageSize && (
        <NoddiBasicCard className='p-4'>
          <TablePagination
            loading={isFetching}
            pagination={pagination}
            onChange={onPaginationChange}
            totalCount={bookings.count}
            translations={getCommonTranslations().table}
          />
        </NoddiBasicCard>
      )}
    </div>
  );
};
